import React, { useEffect, useState, useCallback } from "react";
import Api from "../../services/api";
import {
  Box,
  Heading,
  Flex,
  Text,
  Button,
  Spinner,
  Wrap,
  WrapItem,
  useTheme,
  Input,
  Icon,
} from "@chakra-ui/react";
import { AiOutlineFilter, AiOutlineClear } from "react-icons/ai";
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LabelList,
} from "recharts";

// ─── Helpers ────────────────────────────────────────────────────────────────

const fmtBRL = (value) =>
  typeof value === "number"
    ? value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
    : value;

const fmtK = (value) =>
  value >= 1000 ? `R$${(value / 1000).toFixed(0)}k` : `R$${value}`;

// ─── Custom Bar Shape ────────────────────────────────────────────────────────

const MinHeightBar = (props) => {
  const { x, y, width, height, fill, radius } = props;
  if (!fill || fill === "none" || height <= 0) return null;

  // Garantir altura mínima de 3px para valores > 0
  const minHeight = 3;
  const finalHeight = Math.max(height, minHeight);
  // Ajustar Y para compensar a altura mínima (cresce para cima)
  const finalY = height < minHeight ? y - (minHeight - height) : y;

  // Helper para gerar o path do retângulo com border radius (simplificado)
  const getPath = (x, y, width, height, radius) => {
    const [tl, tr, br, bl] = radius || [0, 0, 0, 0];
    return `
      M${x + tl},${y}
      h${width - tl - tr}
      a${tr},${tr} 0 0 1 ${tr},${tr}
      v${height - tr - br}
      a${br},${br} 0 0 1 -${br},${br}
      h${-(width - br - bl)}
      a${bl},${bl} 0 0 1 -${bl},-${bl}
      v${-(height - bl - tl)}
      a${tl},${tl} 0 0 1 ${tl},-${tl}
      z
    `;
  };

  return <path d={getPath(x, finalY, width, finalHeight, radius)} fill={fill} />;
};

// ─── Custom Tooltip ──────────────────────────────────────────────────────────

const BillingTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  const data = payload[0]?.payload;
  if (!data) return null;

  return (
    <Box
      bg="gray.900"
      border="1px solid"
      borderColor="secondary"
      borderRadius="md"
      p={3}
      boxShadow="lg"
      minW="200px"
    >
      <Text color="white" fontWeight="bold" mb={2}>
        {label}
      </Text>
      {(data.porTag || []).map((t) => (
        <Text key={t.tagId} fontSize="sm" color={t.cor} mb={1}>
          {t.nome}: {fmtBRL(t.valor)} ({t.percentual}%)
        </Text>
      ))}
      <Text color="white" fontSize="sm" fontWeight="bold" mt={2} borderTop="1px solid" borderColor="gray.700" pt={2}>
        Total: {fmtBRL(data.total || 0)}
      </Text>
    </Box>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────

const BillingByTagsChart = () => {
  const theme = useTheme();

  const [availableTags, setAvailableTags] = useState([]);
  const [loadingTags, setLoadingTags] = useState(true);
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [meses, setMeses] = useState(12);
  const [chartStartDate, setChartStartDate] = useState("");
  const [chartEndDate, setChartEndDate] = useState("");

  const [chartData, setChartData] = useState(null); // { tags, meses }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // 1. Fetch tags once on mount
  useEffect(() => {
    setLoadingTags(true);
    Api.get("/api/financeiro/tags")
      .then((res) => {
        const tags = res.data || [];
        const tagsComSemTag = [...tags, { id: 0, nome: "Sem Tag", cor: "#888888" }];
        setAvailableTags(tagsComSemTag);
        
        // Initial selection: all real tags
        const realIds = tags.map((t) => t.id);
        setSelectedTagIds(realIds);
        
        // Initial data fetch
        fetchChartData(realIds, meses);
      })
      .catch((err) => console.error("Erro ao carregar tags:", err))
      .finally(() => setLoadingTags(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run ONLY once on mount

  const fetchChartData = useCallback(async (tagIds, numMeses, sDate = "", eDate = "") => {
    setLoading(true);
    setError(null);
    try {
      const params = { 
        meses: numMeses, 
        tags: tagIds.join(","),
        startDate: sDate,
        endDate: eDate
      };
      const res = await Api.get("/api/financeiro/faturamento-mensal", { params });
      setChartData(res.data);
    } catch (err) {
      console.error("Erro ao carregar faturamento por tags:", err);
      setError("Não foi possível carregar os dados.");
    } finally {
      setLoading(false);
    }
  }, []);

  const toggleTag = useCallback((id) => {
    setSelectedTagIds((prev) =>
      prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]
    );
  }, []);

  const handleGenerate = () => {
    fetchChartData(selectedTagIds, meses, chartStartDate, chartEndDate);
  };

  const handleClear = () => {
    const defaultMeses = 12;
    setMeses(defaultMeses);
    setChartStartDate("");
    setChartEndDate("");
    
    // Default selection: all real tags (excluding Sem Tag with ID 0)
    const realIds = availableTags.filter((t) => t.id !== 0).map((t) => t.id);
    setSelectedTagIds(realIds);
    
    fetchChartData(realIds, defaultMeses, "", "");
  };

  // Build chart-ready data: each entry = one month with one key per tagId
  const rawRows = (chartData?.meses || []).map((m) => {
    const row = { name: m.label, total: m.total, porTag: m.porTag };
    m.porTag.forEach((t) => {
      row[`tag_${t.tagId}`] = t.valor;
      row[`pct_${t.tagId}`] = t.percentual;
    });
    return row;
  });

  // Ensure "faithful" month count (history only for shortcuts)
  const chartRows = rawRows;

  const tags = chartData?.tags || [];

  // Dynamic chart title
  const tagNames = tags.map((t) => t.nome).join(" + ");
  const chartTitle = tagNames
    ? `Faturamento por Mês — Total = ${tagNames}`
    : "Faturamento por Mês";

  // Is the last bar in the stack? (for rounded top corners)
  const lastTagIndex = tags.length - 1;

  return (
    <Box
      bg="surface"
      p={6}
      borderRadius="lg"
      border="1px solid"
      borderColor="gray.800"
    >
      <Heading as="h3" size="sm" color="white" mb={4}>
        Faturamento por Mês — por Tag
      </Heading>

      {/* ── Filters ── */}
      <Flex direction={{ base: "column", md: "row" }} gap={4} mb={6} align={{ md: "flex-end" }} wrap="wrap">
        {/* Tag chips */}
        <Box flex="1">
          <Text fontSize="xs" color="gray.400" mb={2} fontWeight="semibold" textTransform="uppercase" letterSpacing="wide">
            Selecione as Tags
          </Text>
          {loadingTags ? (
            <Spinner size="sm" color="green.400" />
          ) : availableTags.length === 0 ? (
            <Text fontSize="sm" color="gray.500">Nenhuma tag ativa encontrada.</Text>
          ) : (
            <Wrap spacing={2}>
              {availableTags.map((tag) => {
                const isSelected = selectedTagIds.includes(tag.id);
                return (
                  <WrapItem key={tag.id}>
                    <Box
                      as="button"
                      type="button"
                      onClick={() => toggleTag(tag.id)}
                      px={3}
                      py={1}
                      borderRadius="full"
                      fontSize="xs"
                      fontWeight="bold"
                      fontFamily="inherit"
                      lineHeight="normal"
                      cursor="pointer"
                      border="2px solid"
                      borderColor={isSelected ? tag.cor : "gray.600"}
                      bg={isSelected ? `${tag.cor}22` : "transparent"}
                      color={isSelected ? tag.cor : "gray.400"}
                      _hover={{ borderColor: tag.cor, color: tag.cor }}
                      transition="all 0.15s"
                      style={{ borderColor: isSelected ? tag.cor : undefined }}
                    >
                      {isSelected && "✓ "}
                      {tag.nome}
                    </Box>
                  </WrapItem>
                );
              })}
            </Wrap>
          )}
        </Box>

        {/* Date Filters */}
        <Flex gap={3} direction="row" align="flex-end">
          <Box>
            <Text fontSize="xs" color="gray.400" mb={1} fontWeight="semibold">Data Inicial</Text>
            <Input
              type="date"
              size="sm"
              bg="gray.700"
              color="white"
              value={chartStartDate}
              onChange={(e) => setChartStartDate(e.target.value)}
              borderRadius="md"
              borderColor="gray.600"
              _hover={{ borderColor: "gray.500" }}
              _focus={{ borderColor: "green.500", boxShadow: "0 0 0 1px var(--chakra-colors-green-500)" }}
              minW="130px"
              sx={{ "::-webkit-calendar-picker-indicator": { filter: "invert(1)" } }}
            />
          </Box>
          <Box>
            <Text fontSize="xs" color="gray.400" mb={1} fontWeight="semibold">Data Final</Text>
            <Input
              type="date"
              size="sm"
              bg="gray.700"
              color="white"
              value={chartEndDate}
              onChange={(e) => setChartEndDate(e.target.value)}
              borderRadius="md"
              borderColor="gray.600"
              _hover={{ borderColor: "gray.500" }}
              _focus={{ borderColor: "green.500", boxShadow: "0 0 0 1px var(--chakra-colors-green-500)" }}
              minW="130px"
              sx={{ "::-webkit-calendar-picker-indicator": { filter: "invert(1)" } }}
            />
          </Box>
        </Flex>


        {/* Actions */}
        <Flex direction="column" gap={2}>
          <Button
            colorScheme="green"
            size="sm"
            onClick={handleGenerate}
            isDisabled={selectedTagIds.length === 0 || loading}
            isLoading={loading}
            loadingText="Carregando..."
            minW="120px"
            leftIcon={<Icon as={AiOutlineFilter} />}
          >
            Filtrar
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleClear}
            isDisabled={loading}
            minW="120px"
            leftIcon={<Icon as={AiOutlineClear} />}
            borderColor="gray.600"
            color="gray.400"
            _hover={{ bg: "gray.700", color: "white" }}
          >
            Limpar
          </Button>
        </Flex>
      </Flex>

      {/* ── Chart ── */}
      {error && (
        <Flex justify="center" align="center" h="200px">
          <Text color="red.400">{error}</Text>
        </Flex>
      )}

      {!error && !chartData && !loading && (
        <Flex justify="center" align="center" h="200px" direction="column" gap={2}>
          <Text color="gray.500" fontSize="sm">
            Selecione as tags e clique em "Gerar Gráfico"
          </Text>
        </Flex>
      )}

      {!error && chartData && chartRows.length === 0 && (
        <Flex justify="center" align="center" h="200px">
          <Text color="gray.500">Sem dados para as tags e período selecionados.</Text>
        </Flex>
      )}

      {!error && chartData && chartRows.length > 0 && (
        <>
          <Heading as="h4" size="xs" color="gray.400" mb={4} fontWeight="normal">
            {chartTitle}
          </Heading>
          <ResponsiveContainer width="100%" height={380}>
            <ComposedChart
              data={chartRows}
              margin={{ top: 24, right: 30, left: 20, bottom: 20 }}
              barCategoryGap="10%"
            >
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
              <XAxis
                dataKey="name"
                stroke={theme.colors?.gray?.[700] || "#4A5568"}
                tick={{ fill: theme.colors?.gray?.[400] || "#A0AEC0", fontSize: 12 }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                stroke={theme.colors?.gray?.[700] || "#4A5568"}
                tick={{ fill: theme.colors?.gray?.[400] || "#A0AEC0", fontSize: 12 }}
                tickFormatter={fmtK}
                axisLine={false}
              />
              <Tooltip content={<BillingTooltip />} wrapperStyle={{ zIndex: 100 }} />
              <Legend
                verticalAlign="top"
                align="left"
                iconType="circle"
                wrapperStyle={{
                  paddingBottom: "12px",
                  fontSize: "12px",
                  color: theme.colors?.gray?.[400] || "#A0AEC0",
                }}
              />

              {/* Stacked bars — one per tag */}
              {tags.map((tag, idx) => (
                <Bar
                  key={tag.id}
                  dataKey={`tag_${tag.id}`}
                  name={tag.nome}
                  stackId="a"
                  fill={tag.cor}
                  radius={idx === lastTagIndex ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                  shape={<MinHeightBar />}
                >
                  <LabelList
                    position="center"
                    fill="white"
                    fontSize={10}
                    content={({ x, y, width, height, index }) => {
                      const row = chartRows[index];
                      const tagVal = row?.[`tag_${tag.id}`];
                      const pct = row?.[`pct_${tag.id}`];
                      
                      // Don't show labels in very small segments to avoid clutter
                      // Space threshold: 30px to ensure text fits comfortably
                      if (!tagVal || height < 30) return null;
                      
                      return (
                        <text
                          x={x + width / 2}
                          y={y + height / 2}
                          fill="white"
                          textAnchor="middle"
                          dominantBaseline="middle"
                          fontSize={10}
                          fontWeight="bold"
                        >
                          {pct}%
                        </text>
                      );
                    }}
                  />
                </Bar>
              ))}

              {/* Total line */}
              <Line
                type="monotone"
                dataKey="total"
                name="TOTAL"
                stroke="#48BB78"
                strokeWidth={2}
                dot={{ r: 4, fill: "#48BB78" }}
                activeDot={{ r: 6 }}
              >
              </Line>
            </ComposedChart>
          </ResponsiveContainer>
        </>
      )}
    </Box>
  );
};

export default React.memo(BillingByTagsChart);
